import { useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { forceLogout } from './store/slices/authSlice';
import { SessionManager } from './utils/SessionManager';
import AuthBootstrap from './components/AuthBootstrap';
import PopupAdvertisement from './components/popup/PopupAdvertisement';
import NotFound from './pages/NotFound';
import Onboarding from './pages/Onboarding';
import HomePage from './pages/HomePage';
import ThankPage from './pages/ThankPage';
import Authpage from './pages/Authpage';
import Profilling from './pages/Profilling';
import KidProfilePage from './pages/KidProfilePage';
import VideoDetails from './pages/VideoDetails';
import PDFViewer from './pages/PDFViewer';
import ArticleList from './pages/ArticleList';
import ArticleDetails from './pages/ArticleDetails';
import Cart from './pages/Cart';
import MyOrders from './pages/MyOrders';
import NeuroResultsPage from './pages/NeuroResultsPage';
import EstablishmentDashboardPage from './pages/EstablishmentDashboardPage';
import ConfidentialitePage from './pages/legal/ConfidentialitePage';
import ConditionsPage from './pages/legal/ConditionsPage';
import SuppressionComptePage from './pages/legal/SuppressionComptePage';
import ContactPage from './pages/legal/ContactPage';
import ProtectedRoute from './components/ProtectedRoute';
import './App.css';
import './assets/styles/AchikoFont.css';

const ProfilingRedirect = () => {
  const { kidId } = useParams();
  return <Navigate to={kidId ? `/Profilling/${kidId}` : '/Profilling'} replace />;
};

const AppRouter = () => {
  if (!SessionManager.hasVisited()) {
    return <Navigate to="/onboarding" replace />;
  }

  return <HomePage />;
};

const GlobalErrorHandler = ({ children }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const handleApiError = (event) => {
      const { status } = event.detail;
      if (status === 401) {
        dispatch(forceLogout());
      }
    };

    window.addEventListener('apiError', handleApiError);
    return () => {
      window.removeEventListener('apiError', handleApiError);
    };
  }, [dispatch]);

  return children;
};

function App() {
  return (
    <GlobalErrorHandler>
      <AuthBootstrap>
        <ToastContainer
          position="top-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
          style={{ zIndex: 100000 }}
        />
        <PopupAdvertisement />
        <Routes>
          <Route path="/" element={<AppRouter />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/HomePage" element={<HomePage />} />
          <Route path="/auth" element={<Authpage />} />
          <Route path="/Profiling" element={<Navigate to="/Profilling" replace />} />
          <Route path="/Profiling/:kidId" element={<ProfilingRedirect />} />

          <Route path="/articles" element={<ArticleList />} />
          <Route path="/article/:id" element={<ArticleDetails />} />

          {/* Pages publiques Play Store / App Store */}
          <Route path="/confidentialite" element={<ConfidentialitePage />} />
          <Route path="/conditions" element={<ConditionsPage />} />
          <Route path="/suppression-compte" element={<SuppressionComptePage />} />
          <Route path="/contact" element={<ContactPage />} />

          <Route path="/Profilling" element={<ProtectedRoute><Profilling /></ProtectedRoute>} />
          <Route path="/Profilling/:kidId" element={<ProtectedRoute><Profilling /></ProtectedRoute>} />
          <Route path="/Thanks" element={<ProtectedRoute><ThankPage /></ProtectedRoute>} />
          <Route path="/profile/:kidId" element={<ProtectedRoute><KidProfilePage /></ProtectedRoute>} />
          <Route path="/video/:videoId" element={<ProtectedRoute><VideoDetails /></ProtectedRoute>} />
          <Route path="/pdf/:productId" element={<ProtectedRoute><PDFViewer /></ProtectedRoute>} />
          <Route path="/cart" element={<ProtectedRoute><Cart /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
          <Route path="/neuro/results/:evaluationId" element={<ProtectedRoute><NeuroResultsPage /></ProtectedRoute>} />
          <Route path="/establishment" element={<ProtectedRoute><EstablishmentDashboardPage /></ProtectedRoute>} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </AuthBootstrap>
    </GlobalErrorHandler>
  );
}

export default App;
